import React, { useEffect, useMemo, useState } from "react";
import "./DD.css";

export default function DDAccount() {
  const [formData, setFormData] = useState({
    ddNumber: "",
    drawerName: "",
    beneficiaryName: "",
    amount: "",
    branch: "",
    paymentMode: "",
    chequeNo: "",
    cashNo: "",
    transferAcc: "",
    ifsc: "",
    upiId: "",
    UTRNO: "",
    issueDate: "",
    validityDate: "",
    validityDays: "90",
    mobile: "",
    remarks: "",
    status: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // ---------- Helpers ----------
  const toINR = (v) =>
    (Number(v || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  const autoReceiptNo = useMemo(
    () => String(Math.floor(10000000 + Math.random() * 90000000)),
    []
  );

  const printDateTime = useMemo(
    () =>
      new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  // ---------- Auto: Validity from Issue Date + validityDays ----------
  useEffect(() => {
    if (formData.issueDate && formData.validityDays) {
      const issue = new Date(formData.issueDate);
      const exp = new Date(issue);
      exp.setDate(issue.getDate() + Number(formData.validityDays));
      const iso = exp.toISOString().split("T")[0];
      setFormData((p) => ({ ...p, validityDate: iso }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.issueDate, formData.validityDays]);

  // ---------- Change Handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special formatting
    if (name === "ifsc") {
      setFormData({ ...formData, ifsc: value.toUpperCase().trim() });
      return;
    }
    if (name === "upiId") {
      setFormData({ ...formData, upiId: value.trim() });
      return;
    }
    if (name === "amount") {
      setFormData({ ...formData, amount: value.replace(/^0+/, "") });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  // ---------- Validation ----------
  const validate = () => {
    const e = {};

    if (!formData.ddNumber?.trim()) e.ddNumber = "DD Number is required";
    if (!formData.drawerName?.trim()) e.drawerName = "Drawer Name is required";
    if (!formData.beneficiaryName?.trim())
      e.beneficiaryName = "Beneficiary Name is required";

    if (!formData.amount || Number(formData.amount) <= 0)
      e.amount = "Enter a valid amount";

    if (!formData.branch) e.branch = "Select a branch";

    if (!formData.issueDate) e.issueDate = "Issue Date is required";
    if (!formData.validityDate) e.validityDate = "Validity Date is required";

    if (!formData.mobile || !/^[6-9]\d{9}$/.test(formData.mobile))
      e.mobile = "Enter a valid 10-digit mobile";

    if (!formData.status) e.status = "Select status";

    if (!formData.paymentMode) e.paymentMode = "Select a payment mode";

    // Payment specific
    if (formData.paymentMode === "Cash") {
      if (!formData.cashNo?.trim()) e.cashNo = "Cash number is required";
    }
    if (formData.paymentMode === "Cheque") {
      if (!formData.chequeNo?.trim()) e.chequeNo = "Cheque number is required";
    }
    if (formData.paymentMode === "Transfer") {
      if (!formData.transferAcc?.trim())
        e.transferAcc = "Account number is required";
      if (
        !formData.ifsc ||
        !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc.toUpperCase())
      )
        e.ifsc = "Enter a valid IFSC (e.g., HDFC0001234)";
    }
    if (formData.paymentMode === "UPI") {
      if (
        !formData.upiId ||
        !/^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(formData.upiId)
      )
        e.upiId = "Enter a valid UPI ID (e.g., name@upi)";
      if (!formData.UTRNO?.trim()) e.UTRNO = "UTR Number is required";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.ddNumber &&
      formData.drawerName &&
      formData.beneficiaryName &&
      formData.amount &&
      Number(formData.amount) > 0 &&
      formData.branch &&
      formData.issueDate &&
      formData.validityDate &&
      /^[6-9]\d{9}$/.test(formData.mobile) &&
      formData.status &&
      formData.paymentMode &&
      (formData.paymentMode !== "Cash" || formData.cashNo) &&
      (formData.paymentMode !== "Cheque" || formData.chequeNo) &&
      (formData.paymentMode !== "Transfer" ||
        (formData.transferAcc &&
          formData.ifsc &&
          /^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc))) &&
      (formData.paymentMode !== "UPI" ||
        (formData.upiId &&
          /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(formData.upiId) &&
          formData.UTRNO))
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) setSubmitted(true);
  };

  // ---------- Receipt (Customer + Bank copy) ----------
  if (submitted) {
    const Row = ({ label, value, mono }) => (
      <tr>
        <td>
          <strong>{label}</strong>
        </td>
        <td className={mono ? "mono" : ""}>{value || "-"}</td>
      </tr>
    );

    const Receipt = ({ copyType }) => (
      <div className="ddreceipt-copy">
        <div className="ddreceipt-header">
          <img src="/bank-logo.png" alt="Bank Logo" className="ddbank-logo" />
          <div className="ddreceipt-header-text">
            <h2 className="ddreceipt-title">Demand Draft Receipt</h2>
            <p className="ddcopy-pill">{copyType}</p>
            <p>
              <strong>Receipt No:</strong> {autoReceiptNo}
            </p>
            <p>
              <strong>Print Date:</strong> {printDateTime}
            </p>
          </div>
        </div>

        <table className="ddreceipt-table">
          <tbody>
            <Row label="DD Number" value={formData.ddNumber} />
            <Row label="Drawer Name" value={formData.drawerName} />
            <Row label="Beneficiary Name" value={formData.beneficiaryName} />
            <Row label="Amount" value={`₹ ${toINR(formData.amount)}`} mono />
            <Row label="Branch" value={formData.branch} />
            <Row label="Issue Date" value={formData.issueDate} />
            <Row
              label={`Validity Date (+${formData.validityDays} days)`}
              value={formData.validityDate}
            />
            <Row label="Mobile" value={formData.mobile} mono />
            <Row label="Payment Mode" value={formData.paymentMode} />

            {formData.paymentMode === "Cash" && (
              <Row label="Cash No" value={formData.cashNo} mono />
            )}

            {formData.paymentMode === "Cheque" && (
              <Row label="Cheque No" value={formData.chequeNo} mono />
            )}
            {formData.paymentMode === "Transfer" && (
              <>
                <Row label="Account No" value={formData.transferAcc} mono />
                <Row label="IFSC" value={formData.ifsc} mono />
              </>
            )}
            {formData.paymentMode === "UPI" && (
              <>
                <Row label="UPI ID" value={formData.upiId} mono />
                <Row label="UTR No" value={formData.UTRNO} mono />
              </>
            )}

            <Row label="Status" value={formData.status} />
            <Row label="Remarks" value={formData.remarks} />
          </tbody>
        </table>

        <div className="ddsignature-section">
          <div className="ddsignature-line" />
          <p>Authorized Signatory</p>
        </div>
      </div>
    );

    return (
      <div className="ddreceipt-container">
        <Receipt copyType="Customer Copy" />
        <hr className="ddreceipt-divider" />
        <Receipt copyType="Bank Copy" />

        <div className="ddreceipt-actions">
          <button onClick={() => window.print()} className="dd-btn primary">
            🖨️ Print
          </button>
          <button
            onClick={() => setSubmitted(false)}
            className="btn ghost"
          >
            ⬅️ Back to Form
          </button>
        </div>
      </div>
    );
  }

  // ---------- Form ----------
  return (
    <div className="dd-container">
      <h2 className="dd-title">Demand Draft Request Form</h2>

      <form onSubmit={handleSubmit} className="dd-form" noValidate>
        {/* Row 1 */}
        <div className="dd-row">
          <div className={`dd-field ${errors.ddNumber ? "error-field" : ""}`}>
            <label>DD Number :</label>
            <input
              type="text"
              name="ddNumber"
              value={formData.ddNumber}
              onChange={handleChange}
              placeholder="Enter DD Number"
              autoComplete="off"
            />
            {errors.ddNumber && <span className="dd-error">{errors.ddNumber}</span>}
          </div>

          <div className={`dd-field ${errors.drawerName ? "error-field" : ""}`}>
            <label>Drawer Name :</label>
            <input
              type="text"
              name="drawerName"
              value={formData.drawerName}
              onChange={handleChange}
              placeholder="Enter Drawer Name"
            />
            {errors.drawerName && <span className="dd-error">{errors.drawerName}</span>}
          </div>

          <div
            className={`dd-field ${errors.beneficiaryName ? "error-field" : ""}`}
          >
            <label>Beneficiary Name :</label>
            <input
              type="text"
              name="beneficiaryName"
              value={formData.beneficiaryName}
              onChange={handleChange}
              placeholder="Enter Beneficiary Name"
            />
            {errors.beneficiaryName && (
              <span className="dd-error">{errors.beneficiaryName}</span>
            )}
          </div>

          <div className={`dd-field ${errors.amount ? "error-field" : ""}`}>
            <label>Amount (₹) :</label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter Amount"
              inputMode="numeric"
              min="1"
            />
            {errors.amount && <span className="dd-error">{errors.amount}</span>}
          </div>
        </div>

        {/* Row 2 */}
        <div className="dd-row">
          <div className={`dd-field ${errors.branch ? "error-field" : ""}`}>
            <label>Branch :</label>
            <select name="branch" value={formData.branch} onChange={handleChange}>
              <option value="">Select Branch</option>
              <option value="Delhi">Delhi</option>
              <option value="Mumbai">Mumbai</option>
              <option value="Patna">Patna</option>
            </select>
            {errors.branch && <span className="dd-error">{errors.branch}</span>}
          </div>

          <div className={`dd-field ${errors.issueDate ? "error-field" : ""}`}>
            <label>Issue Date :</label>
            <input
              type="date"
              name="issueDate"
              value={formData.issueDate}
              onChange={handleChange}
            />
            {errors.issueDate && <span className="dd-error">{errors.issueDate}</span>}
          </div>

          <div className="dd-field">
            <label>Validity (days) :</label>
            <select
              name="validityDays"
              value={formData.validityDays}
              onChange={handleChange}
            >
              <option value="30">30</option>
              <option value="60">60</option>
              <option value="90">90</option>
              <option value="120">120</option>
            </select>
          </div>

          <div
            className={`dd-field ${errors.validityDate ? "error-field" : ""}`}
          >
            <label>Validity Date :</label>
            <input
              type="date"
              name="validityDate"
              value={formData.validityDate}
              onChange={handleChange}
            />
            {errors.validityDate && (
              <span className="dd-error">{errors.validityDate}</span>
            )}
          </div>
        </div>

        {/* Row 3 */}
        <div className="dd-row">
          <div className={`dd-field ${errors.mobile ? "error-field" : ""}`}>
            <label>Mobile Number :</label>
            <input
              type="text"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="10-digit mobile"
              maxLength={10}
              inputMode="numeric"
            />
            {errors.mobile && <span className="dd-error">{errors.mobile}</span>}
          </div>

          <div
            className={`dd-field ${errors.paymentMode ? "error-field" : ""}`}
          >
            <label>Payment Mode :</label>
            <select
              name="paymentMode"
              value={formData.paymentMode}
              onChange={handleChange}
            >
              <option value="">Select Payment Mode</option>
              <option value="Cash">Cash</option>
              <option value="Cheque">Cheque</option>
              <option value="Transfer">Transfer</option>
              <option value="UPI">UPI</option>
            </select>
            {errors.paymentMode && (
              <span className="dd-error">{errors.paymentMode}</span>
            )}
          </div>

          {/* Conditional fields */}
          {formData.paymentMode === "Cash" && (
            <div className={`dd-field ${errors.cashNo ? "error-field" : ""}`}>
              <label>Cash Number</label>
              <input
                type="text"
                name="cashNo"
                value={formData.cashNo}
                onChange={handleChange}
                placeholder="Enter Cash Number"
              />
              {errors.cashNo && <span className="dd-error">{errors.cashNo}</span>}
            </div>
          )}

          {formData.paymentMode === "Cheque" && (
            <div className={`dd-field ${errors.chequeNo ? "error-field" : ""}`}>
              <label>Cheque Number</label>
              <input
                type="text"
                name="chequeNo"
                value={formData.chequeNo}
                onChange={handleChange}
                placeholder="Enter Cheque Number"
              />
              {errors.chequeNo && <span className="dd-error">{errors.chequeNo}</span>}
            </div>
          )}

          {formData.paymentMode === "Transfer" && (
            <>
              <div
                className={`dd-field ${errors.transferAcc ? "error-field" : ""}`}
              >
                <label>Account Number</label>
                <input
                  type="text"
                  name="transferAcc"
                  value={formData.transferAcc}
                  onChange={handleChange}
                  placeholder="Enter Account Number"
                />
                {errors.transferAcc && (
                  <span className="dd-error">{errors.transferAcc}</span>
                )}
              </div>
              <div className={`dd-field ${errors.ifsc ? "error-field" : ""}`}>
                <label>IFSC Code</label>
                <input
                  type="text"
                  name="ifsc"
                  value={formData.ifsc}
                  onChange={handleChange}
                  placeholder="e.g., HDFC0001234"
                />
                {errors.ifsc && <span className="dd-error">{errors.ifsc}</span>}
              </div>
            </>
          )}

          {formData.paymentMode === "UPI" && (
            <>
              <div className={`dd-field ${errors.upiId ? "error-field" : ""}`}>
                <label>UPI ID</label>
                <input
                  type="text"
                  name="upiId"
                  value={formData.upiId}
                  onChange={handleChange}
                  placeholder="example@upi"
                />
                {errors.upiId && <span className="dd-error">{errors.upiId}</span>}
              </div>

              <div className={`dd-field ${errors.UTRNO ? "error-field" : ""}`}>
                <label>UTR No.</label>
                <input
                  type="text"
                  name="UTRNO"
                  value={formData.UTRNO}
                  onChange={handleChange}
                  placeholder="Enter UTR Number"
                />
                {errors.UTRNO && <span className="dd-error">{errors.UTRNO}</span>}
              </div>
            </>
          )}
        </div>

        {/* Row 4 */}
        <div className="dd-row">
          <div className="dd-field" style={{ gridColumn: "span 3" }}>
            <label>Remarks :</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows={2}
              placeholder="Any special instructions…"
            />
          </div>

          <div className={`dd-field ${errors.status ? "error-field" : ""}`}>
            <label>Status :</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="">Select Status</option>
              <option value="Issued">Issued</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Paid">Paid</option>
            </select>
            {errors.status && <span className="dd-error">{errors.status}</span>}
          </div>
        </div>

        <div className="dd-submit">
          <button
            type="submit"
            className="ddbtn primary"
            disabled={!isFormValid()}
          >
            Submit Request
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setFormData({
                ddNumber: "",
                drawerName: "",
                beneficiaryName: "",
                amount: "",
                branch: "",
                paymentMode: "",
                chequeNo: "",
                cashNo: "",
                transferAcc: "",
                ifsc: "",
                upiId: "",
                UTRNO: "",
                issueDate: "",
                validityDate: "",
                validityDays: "90",
                mobile: "",
                remarks: "",
                status: "",
              });
              setErrors({});
            }}
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}
